var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SaharaRules_1;
import { ArrayUtils, MGPMap, MGPOptional, MGPValidation } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { CoordFailure } from '../../jscaip/Coord';
import { CoordSet } from '../../jscaip/CoordSet';
import { FourStatePiece } from '../../jscaip/FourStatePiece';
import { GameStatus } from '../../jscaip/GameStatus';
import { HexagonalUtils } from '../../jscaip/HexagonalUtils';
import { Player } from '../../jscaip/Player';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TriangularCheckerBoard } from '../../jscaip/state/TriangularCheckerBoard';
import { TriangularGameState } from '../../jscaip/state/TriangularGameState';
import { Debug } from '../../utils/Debug';
import { SaharaFailure } from './SaharaFailure';
import { SaharaState } from './SaharaState';
export class SaharaNode extends GameNode {
}
let SaharaRules = class SaharaRules extends Rules {
    static { SaharaRules_1 = this; }
    static singleton = MGPOptional.empty();
    static get() {
        if (SaharaRules_1.singleton.isAbsent()) {
            SaharaRules_1.singleton = MGPOptional.of(new SaharaRules_1());
        }
        return SaharaRules_1.singleton.get();
    }
    getInitialState() {
        const size = 3;
        const N = FourStatePiece.UNREACHABLE;
        const O = FourStatePiece.ZERO;
        const X = FourStatePiece.ONE;
        const _ = FourStatePiece.EMPTY;
        const board = HexagonalUtils.createBoard(size, N, _);
        const start = (size + 1) % 2;
        const xEnd = (4 * size) - (2 - start);
        const yEnd = (size * 2) - 1;
        const first = size - (size % 2);
        const second = first + 1;
        const third = first + (2 * size) - 1;
        const fourth = third + 1;
        board[0][first] = O;
        board[0][second] = X;
        board[0][third] = O;
        board[0][fourth] = X;
        board[first - start][start] = X;
        board[second - start][start] = O;
        board[first - start][xEnd] = O;
        board[second - start][xEnd] = X;
        board[yEnd][first] = O;
        board[yEnd][second] = X;
        board[yEnd][third] = O;
        board[yEnd][fourth] = X;
        return new SaharaState(board, 0);
    }
    getStartingCoords(state, player) {
        const startingCoords = [];
        for (const coordAndContent of state.getCoordsAndContents()) {
            if (coordAndContent.content.is(player)) {
                startingCoords.push(coordAndContent.coord);
            }
        }
        return startingCoords;
    }
    getBoardValueByPiece(state, player) {
        const playersPiece = this.getStartingCoords(state, player);
        const playerFreedoms = new MGPMap();
        for (const piece of playersPiece) {
            const freedoms = TriangularGameState.getEmptyNeighbors(state.board, piece, FourStatePiece.EMPTY).length;
            playerFreedoms.set(piece, freedoms);
        }
        return playerFreedoms;
    }
    /**
     * @param state the evaluated state
     * @param player the player for which returned values apply
     * @returns a sorted table of the freedom of player's pieces
     */
    getBoardValuesFor(state, player) {
        const playerFreedomsMap = this.getBoardValueByPiece(state, player);
        const playerFreedomsValue = playerFreedomsMap.getValueList();
        ArrayUtils.sortByDescending(playerFreedomsValue, (value) => -value);
        return playerFreedomsValue;
    }
    applyLegalMove(move, state, _config, _info) {
        const board = state.getCopiedBoard();
        board[move.getEnd().y][move.getEnd().x] = board[move.getStart().y][move.getStart().x];
        board[move.getStart().y][move.getStart().x] = FourStatePiece.EMPTY;
        const resultingState = new SaharaState(board, state.turn + 1);
        return resultingState;
    }
    isLegal(move, state) {
        const coordsValidity = this.getCoordsValidity(move, state);
        if (coordsValidity.isFailure()) {
            return coordsValidity;
        }
        const movedPawn = state.getPieceAt(move.getStart());
        if (movedPawn.is(state.getCurrentPlayer()) === false) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        const landingSpace = state.getPieceAt(move.getEnd());
        if (landingSpace !== FourStatePiece.EMPTY) {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        const commonNeighbor = TriangularCheckerBoard.getCommonNeighbor(move.getStart(), move.getEnd());
        if (commonNeighbor.isPresent()) {
            if (state.getPieceAt(commonNeighbor.get()) === FourStatePiece.EMPTY) {
                return MGPValidation.SUCCESS;
            }
            else {
                return MGPValidation.failure(SaharaFailure.CAN_ONLY_REBOUND_ON_EMPTY_SPACE());
            }
        }
        else {
            return MGPValidation.SUCCESS;
        }
    }
    getCoordsValidity(move, state) {
        if (state.isNotOnBoard(move.getStart())) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(move.getStart()));
        }
        else if (state.isNotOnBoard(move.getEnd())) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(move.getEnd()));
        }
        else {
            return MGPValidation.SUCCESS;
        }
    }
    getGameStatus(node) {
        const board = node.gameState;
        const zeroFreedoms = this.getBoardValuesFor(board, Player.ZERO);
        const oneFreedoms = this.getBoardValuesFor(board, Player.ONE);
        return this.getGameStatusFromFreedoms(zeroFreedoms, oneFreedoms);
    }
    getLandingCoordsMatching(coord, state, premise) {
        const landings = new CoordSet(TriangularCheckerBoard.getNeighbors(coord).filter(premise));
        if (TriangularCheckerBoard.isSpaceDark(coord)) {
            return landings.toList();
        }
        else {
            let farLandings = new CoordSet(landings.toList()); // Deep copy
            const emptyNeighbors = landings.filter((c) => state.getPieceAt(c).equals(FourStatePiece.EMPTY));
            for (const neighbor of emptyNeighbors) {
                const secondStepNeighbors = TriangularCheckerBoard.getNeighbors(neighbor).filter(premise);
                for (const secondStepNeighbor of secondStepNeighbors) {
                    farLandings = farLandings.addElement(secondStepNeighbor);
                }
            }
            return farLandings.toList();
        }
    }
    getLegalLandingCoords(state, coord) {
        const isOnBoardAndEmpty = (c) => {
            return state.hasPieceAt(c, FourStatePiece.EMPTY);
        };
        return this.getLandingCoordsMatching(coord, state, isOnBoardAndEmpty);
    }
    getValidLandingCoords(state, coord) {
        const isOnBoardAndReachable = (c) => {
            return state.isOnBoard(c) &&
                state.getPieceAt(c).equals(FourStatePiece.UNREACHABLE) === false;
        };
        return this.getLandingCoordsMatching(coord, state, isOnBoardAndReachable);
    }
    getGameStatusFromFreedoms(zeroFreedoms, oneFreedoms) {
        if (zeroFreedoms[0] === 0) {
            return GameStatus.ONE_WON;
        }
        else if (oneFreedoms[0] === 0) {
            return GameStatus.ZERO_WON;
        }
        return GameStatus.ONGOING;
    }
};
SaharaRules = SaharaRules_1 = __decorate([
    Debug.log
], SaharaRules);
export { SaharaRules };
